import { AuthFacade } from '@lentau/auth/store/store/auth.facade';
import { inject, Injectable, Signal } from '@angular/core';
import { accessTokenCookieName, accessTokenExpiresName, refreshTokenCookieName } from '@lentau/ssr/shared/const';
import { CookieFacade } from '@lentau/ui-kit/core/services/cookie/cookie.facade';
import { SignalMethod, signalMethod } from '@ngrx/signals';
import { DateUtils } from '@lentau/ui-kit/core/utils/date.utils';
import { PlatformService } from '@lentau/ui-kit/core/services/platform/platform.service';
import { GrowthBookService } from '@lentau/core/growth-book/shared/growth-book.service';
import { isAuthTokenExperiment } from '@lentau/auth/shared/experiment';

/**
 * Passport JWT lifecycle (exp_br_1521_refresh_auth_token).
 *
 * SSR must not mint guest passport + Set-Cookie when inbound passport cookies are missing
 * but the browser may still hold HttpOnly user cookies (CookieSsrFacade empty → AGW Cookie "undefined"
 * in CH-24180 / severgroup timeline). Writing guest Set-Cookie would overwrite user passport.
 */
@Injectable({ providedIn: 'root' })
export class AuthTokenService {
  private cookieService: CookieFacade = inject(CookieFacade);
  private authFacade: AuthFacade = inject(AuthFacade);
  private platformService: PlatformService = inject(PlatformService);
  private growthBookService: GrowthBookService = inject(GrowthBookService);

  private isAuthTokenExperiment: Signal<boolean> = isAuthTokenExperiment();

  /** True when document request already had Passport* cookies (read before any guest mint). */
  private inboundPassportCookiesPresent: boolean = false;

  private expiresInSetData: SignalMethod<Date> = signalMethod((expiresIn: Date) => {
    if (!expiresIn) return;

    this.setServerContext();

    this.cookieService.set(accessTokenExpiresName, expiresIn.getTime().toString(), {
      expires: DateUtils.addDays(new Date(), 30),
      path: '/',
    });

    const expireTimeout: number = expiresIn.getTime() - new Date().getTime();
    this.refreshIfTime(expireTimeout - 10000);
  });

  private initAfterGrowthBook: SignalMethod<boolean> = signalMethod((isInit: boolean) => {
    if (this.platformService.isBrowser && !isInit) return;

    if (!this.isAuthTokenExperiment()) return;

    this.setOrGetToken();

    this.expiresInSetData(this.authFacade.tokenExpiresIn);
    this.retryAfterSessionChecked(this.authFacade.authTokenChecked);
  });

  /** CSR: sessionGet is async — retry guest decision after authTokenChecked. */
  private retryAfterSessionChecked: SignalMethod<boolean> = signalMethod((checked: boolean) => {
    if (!this.platformService.isBrowser || !checked || !this.isAuthTokenExperiment()) return;

    this.setOrGetToken();
  });

  public init(): void {
    this.initAfterGrowthBook(this.growthBookService.isInitialized);
  }

  // GR-3599
  public authSessionStart(refresh: string, access: string, expiresStr: string, passportId: string): void {
    const expiresIn: Date = DateUtils.addSecs(new Date(), +expiresStr);

    this.cookieService.set(accessTokenExpiresName, expiresIn.getTime().toString(), {
      expires: DateUtils.addDays(new Date(), 30),
      path: '/',
    });

    this.authFacade.setTokensData(null, null, expiresIn);
    this.authFacade.authSessionStart(refresh, access, passportId);
  }

  private setServerContext(): void {
    if (!this.platformService.isServer) return;

    // Only echo passport cookies back to the HTML response when the document already had them
    // (refresh path). Never Set-Cookie guest tokens onto a response that had empty inbound passport —
    // that overwrites browser HttpOnly user cookies Angular could not read.
    if (!this.inboundPassportCookiesPresent) return;

    this.cookieService.set(accessTokenCookieName, this.authFacade.accessToken(), {
      expires: DateUtils.addDays(new Date(), 30),
      path: '/',
      httpOnly: true,
    });
    this.cookieService.set(refreshTokenCookieName, this.authFacade.refreshToken(), {
      expires: DateUtils.addDays(new Date(), 30),
      path: '/',
      httpOnly: true,
    });
  }

  private setOrGetToken(): void {
    const refresh: string = this.cookieService.get(refreshTokenCookieName);
    const access: string = this.cookieService.get(accessTokenCookieName);
    const expires: string = this.cookieService.get(accessTokenExpiresName);

    this.inboundPassportCookiesPresent = Boolean(refresh || access);

    this.authFacade.setTokensData(refresh, access, expires ? new Date(+expires) : null);

    if (this.platformService.isServer) {
      if (refresh) {
        this.authFacade.refreshPassportToken();
      } else if (access) {
        // Inbound access without refresh: keep store tokens, do not mint guest (would overwrite).
        return;
      } else if (!this.authFacade.isAuth()) {
        this.authFacade.getPassportGuestToken();
      }
      return;
    }

    if (expires || this.authFacade.isAuth()) {
      return;
    }

    // Wait for sessionGet before minting guest — avoids overwriting user HttpOnly passport
    // when GrowthBook init races ahead of authTokenChecked.
    if (!this.authFacade.authTokenChecked()) {
      return;
    }

    this.authFacade.getPassportGuestToken();
  }

  private refreshIfTime(timeoutMs: number): void {
    if (timeoutMs < 0 && !this.authFacade.tokenIsLoading()) this.authFacade.refreshPassportToken();
  }
}
